import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2, Play, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { API_BASE } from '@/api/base';
import { getCameraSnapshot } from '@/api/cameraSnapshot';
import { extractApiError } from '@/utils/apiError';

interface Props {
  cameraId: number | string;
  /** Camera.type — picks the preview transport (stream vs polling). */
  cameraType?: string;
  /** Polling interval in milliseconds. Default 1000 (1 fps). */
  intervalMs?: number;
  className?: string;
}

// Types the BE can remux straight off the camera's RTSP (server-direct,
// no transcode). Everything else — and any stream that fails before the
// first frame (jetson-bound camera, browser without HEVC decode) —
// falls back to 1 fps snapshot polling.
const STREAMABLE_TYPES = new Set(['ezviz', 'rtsp', 'trassir_rtsp']);

/**
 * Live preview panel (issue #34 part 2).
 *
 * Direct-reachable RTSP cameras get a real video stream — the BE remuxes
 * the camera bitstream to fragmented MP4 (``/preview-stream/``) and the
 * browser decodes it. Other cameras poll
 * ``GET /equipment_discovery/cameras/<id>/snapshot/`` at ~1 fps. The
 * active component is unmounted when not playing so an open dialog
 * doesn't burn requests or hold a stream.
 */
export function LiveStreamPanel({ cameraId, cameraType, intervalMs = 1000, className }: Props) {
  const { t } = useT();
  const [playing, setPlaying] = useState(false);

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{t('equipment.liveStream.title')}</p>
          <p className="text-xs text-muted-foreground">
            {t('equipment.liveStream.subtitle')}
          </p>
        </div>
        {playing ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPlaying(false)}
          >
            <Square className="size-3.5" />
            {t('equipment.liveStream.stop')}
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPlaying(true)}
          >
            <Play className="size-3.5" />
            {t('equipment.liveStream.play')}
          </Button>
        )}
      </div>

      {playing ? (
        STREAMABLE_TYPES.has(cameraType ?? '') ? (
          <VideoStream cameraId={cameraId} intervalMs={intervalMs} />
        ) : (
          <Stream cameraId={cameraId} intervalMs={intervalMs} />
        )
      ) : (
        <div className="flex aspect-video w-full items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground">
          {t('equipment.liveStream.idle')}
        </div>
      )}
    </div>
  );
}

/**
 * Direct remuxed-RTSP player. The BE caps each stream at ~2 minutes so
 * an abandoned tab can't hold a server thread — `onEnded` transparently
 * reopens it. A failure before the first decoded frame means this
 * camera/browser combo can't direct-stream (jetson-only reach, no HEVC
 * hardware) — we permanently fall back to snapshot polling for the
 * session instead of error-looping.
 */
function VideoStream({ cameraId, intervalMs }: { cameraId: number | string; intervalMs: number }) {
  const { t } = useT();
  const [nonce, setNonce] = useState(0);
  const [fallback, setFallback] = useState(false);
  const [hasFrame, setHasFrame] = useState(false);

  if (fallback) {
    return <Stream cameraId={cameraId} intervalMs={intervalMs} />;
  }

  let token: string | null = null;
  try {
    token = localStorage.getItem('jwt');
  } catch { /* non-browser env */ }
  const src = `${API_BASE}/equipment_discovery/cameras/${cameraId}/preview-stream/`
    + `?token=${encodeURIComponent(token ?? '')}&n=${nonce}`;

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-md border bg-muted">
      <video
        key={nonce}
        src={src}
        autoPlay
        muted
        playsInline
        className="h-full w-full object-contain"
        onLoadedData={() => setHasFrame(true)}
        onEnded={() => setNonce(n => n + 1)}
        onError={() => {
          if (hasFrame) {
            // Mid-stream hiccup — reconnect after a beat.
            window.setTimeout(() => setNonce(n => n + 1), 1000);
          } else {
            setFallback(true);
          }
        }}
      />
      {!hasFrame && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </span>
        </div>
      )}
    </div>
  );
}

// Cap on the backed-off polling interval when a camera keeps failing.
// Without a cap, a persistently-broken camera (bad creds, NVR RTSP-session
// exhaustion, etc.) gets hammered every `intervalMs` for as long as the
// operator leaves the dialog open — each attempt is a fresh RTSP/HTTP-digest
// probe through the jetson, and on some Hikvision NVRs a burst of failed
// probes trips the device's own anti-bruteforce "illegal login" lock, which
// then also blocks the persistent watcher's *legitimate* reconnects (found
// 2026-07-06 on SurfCoffee PitStop — a stuck snapshot widget knocked out the
// whole zone's capture, not just the preview).
const _MAX_BACKOFF_MS = 15_000;

/** Inner component — does the actual polling. Mount = start, unmount = stop. */
function Stream({ cameraId, intervalMs }: { cameraId: number | string; intervalMs: number }) {
  const { t } = useT();
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState(false);
  const [pending, setPending] = useState(true);
  const inFlightRef = useRef(false);
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;
    // Consecutive-failure counter drives exponential backoff (intervalMs,
    // 2x, 4x, … capped at _MAX_BACKOFF_MS). Resets to 0 on any success so
    // a camera that recovers goes straight back to the requested cadence.
    let consecutiveFailures = 0;

    const scheduleNext = (delayMs: number) => {
      if (cancelled) return;
      timeoutId = window.setTimeout(tick, delayMs);
    };

    const tick = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const resp = await getCameraSnapshot(cameraId);
        if (cancelled) return;
        const url = URL.createObjectURL(resp.data);
        if (lastUrlRef.current) {
          URL.revokeObjectURL(lastUrlRef.current);
        }
        lastUrlRef.current = url;
        setSrc(url);
        setError(null);
        setPending(false);
        consecutiveFailures = 0;
        scheduleNext(intervalMs);
      } catch (e) {
        if (cancelled) return;
        const status = isAxiosError(e) ? e.response?.status : undefined;
        if (status === 422) {
          setUnsupported(true);
          setPending(false);
          return; // permanent — no further scheduling
        }
        setError(
          isAxiosError(e)
            ? extractApiError(e, t('equipment.liveStream.error'))
            : t('equipment.liveStream.error'),
        );
        setPending(false);
        consecutiveFailures += 1;
        const backoff = Math.min(intervalMs * 2 ** consecutiveFailures, _MAX_BACKOFF_MS);
        scheduleNext(backoff);
      } finally {
        inFlightRef.current = false;
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (lastUrlRef.current) {
        URL.revokeObjectURL(lastUrlRef.current);
        lastUrlRef.current = null;
      }
    };
  }, [cameraId, intervalMs, t]);

  if (unsupported) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground">
        {t('equipment.liveStream.unsupported')}
      </div>
    );
  }

  return (
    <>
      <div className="relative aspect-video w-full overflow-hidden rounded-md border bg-muted">
        {src ? (
          <img
            src={src}
            alt={t('equipment.liveStream.title')}
            className="h-full w-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {pending ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="size-4 animate-spin" />
                {t('common.loading')}
              </span>
            ) : (
              t('equipment.liveStream.noFrame')
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs text-destructive">{error}</p>
      )}
    </>
  );
}
